import React, { useState, useEffect } from "react";
import { updateOverlay } from "../utils/backgroundUtils";
import { Theme } from "../types";

interface DarkModeToggleProps {
  variant?: "floating" | "inline";
}

const DarkModeToggle: React.FC<DarkModeToggleProps> = ({ variant = "floating" }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, fallback to light mode
    const saved = localStorage.getItem("theme");
    if (saved && (saved === "light" || saved === "dark")) {
      return saved as Theme;
    }

    // Default to light mode
    return "light";
  });

  const updateTheme = (newTheme: Theme): void => {
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }

    // Update the background overlay after theme change
    setTimeout(updateOverlay, 100);
  };

  const toggleDarkMode = (): void => {
    const nextTheme: Theme = theme === "light" ? "dark" : "light";
    setTheme(nextTheme);
    updateTheme(nextTheme);
  };

  // Initialize theme on mount only
  useEffect(() => {
    updateTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = `Switch to ${theme === "light" ? "dark" : "light"} mode`;

  return (
    <button
      onClick={toggleDarkMode}
      className={`${
        variant === "floating"
          ? "fixed top-4 right-4 sm:top-6 sm:right-6 p-2.5 sm:p-3 rounded-full border shadow-md hover:shadow-lg"
          : "p-1"
      } z-50 active:scale-95 transition-all duration-200`}
      aria-label={label}
      style={
        variant === "floating"
          ? {
              background: "var(--warm-bg)",
              borderColor: "var(--warm-border)",
              color: "var(--warm-text)",
              minWidth: "44px",
              minHeight: "44px",
            }
          : { color: "var(--warm-muted)" }
      }
      title={`Current: ${theme} theme`}
    >
      <div className={variant === "floating" ? "w-5 h-5 sm:w-6 sm:h-6" : "w-4 h-4"}>
        {theme === "dark" ? (
          // Moon icon — stroke, matches other icons
          <svg fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        ) : (
          // Sun icon — stroke, matches other icons
          <svg fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="4" />
            <path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </svg>
        )}
      </div>
    </button>
  );
};

export default DarkModeToggle;
